import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { TextField } from '../../Base/Form'
import { Collapse, Slider, CircularProgress } from '@mui/material'
import MilesNCashOTPModal from './MilesNCashOTPModal'
import {
  setMilesNBonusAccount,
  setMbData,
} from '../../../../js/lib/redux/slices/booking'
import { postData } from '../../../../js/helpers/apiHelper'
import * as lib from '../../../../js/lib/lib'

const MilesNCash = ({
  control,
  errors,
  setValue,
  triggerLoginModal,
  totalPrice,
  isLoading,
}) => {
  const { dictionary, configuration, token } =
    window.bookingData || window.modifyData
  const { milesNBonusAccount, mbData } = useSelector((state) => state.booking)

  const [openMilesNCashOTPModal, setOpenMilesNCashOTPModal] = useState(false)
  const [hasOffer, setHasOffer] = useState(true)
  const [collapseOpen, setCollapseOpen] = useState(true)
  let mbLoggedIn = !!milesNBonusAccount?.fFN || false
  const conversionRate = milesNBonusAccount?.rate || 300

  const dispatch = useDispatch()

  const totalPriceToMiles = () => {
    return Math.round(totalPrice * conversionRate)
  }

  const lang = window.lang
  const minSliderValue = milesNBonusAccount?.minimumMiles || 1500
  const maxSliderValue =
    milesNBonusAccount?.remainingMiles > totalPriceToMiles()
      ? totalPriceToMiles()
      : milesNBonusAccount?.remainingMiles

  const priceToLocale = (number) => {
    return number.toLocaleString(lang, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  useEffect(() => {
    setTimeout(() => {
      setValue('milesNCashInputMiles', selectedMiles)
    }, 0)
  }, [selectedMiles])

  const handleOpenLoginModal = (e) => {
    e.preventDefault()
    triggerLoginModal()
  }

  const handleSliderChange = (event, newValue) => {
    const clampedValue = Math.max(
      minSliderValue,
      Math.min(newValue, maxSliderValue)
    )
    setSelectedMiles(clampedValue)
    setValue('milesNCashInputMiles', clampedValue)
    setInputError('')
  }

  // Initialize with Redux value if available, otherwise use minSliderValue
  const [selectedMiles, setSelectedMiles] = useState(() => {
    const initial =
      mbData?.redeemEnabled && typeof mbData?.redeemAmount === 'number'
        ? mbData.redeemAmount
        : maxSliderValue
    return initial ?? minSliderValue // Always fallback to a number
  })
  const [inputError, setInputError] = useState('')

  // Update selectedMiles when Redux store changes
  useEffect(() => {
    if (mbData?.redeemEnabled && mbData?.redeemAmount) {
      setSelectedMiles(mbData.redeemAmount)
    }
  }, [mbData?.redeemAmount, mbData?.redeemEnabled])

  const handleTextFieldChange = (event) => {
    const newValue = event.target.rawValue.replace(/,/g, '')
    if (/^\d*$/.test(newValue)) {
      const numericValue = newValue === '' ? '' : Number(newValue)
      setValue('milesNCashInputMiles', numericValue)
      if (newValue === '') {
        setInputError('This field cannot be empty')
      } else if (
        numericValue < minSliderValue ||
        numericValue > maxSliderValue
      ) {
        setInputError(
          `Value must be between ${minSliderValue.toLocaleString(
            lang
          )} and ${maxSliderValue.toLocaleString(lang)}`
        )
      } else {
        setSelectedMiles(numericValue)
        setInputError('')
      }
    }
  }

  const stepValue = 1

  const handleButtonClick = (e, type) => {
    e.preventDefault()
    const newValue =
      type === 'increment'
        ? selectedMiles + stepValue
        : selectedMiles - stepValue
    const clampedValue = Math.max(
      minSliderValue,
      Math.min(newValue, maxSliderValue)
    )
    setSelectedMiles(clampedValue)
    setValue('milesNCashInputMiles', clampedValue)
    setInputError('')
  }

  const handleMilesNCashButton = async (e) => {
    e.preventDefault()
    dispatch(setMbData({ key: 'redeemEnabled', value: !mbData.redeemEnabled }))
    dispatch(setMbData({ key: 'redeemAmount', value: selectedMiles }))
    dispatch(
      setMbData({
        key: 'hasRemainingCost',
        value: !mbData.redeemEnabled ? calculateHasRemainingCost() : true,
      })
    )
  }

  const handleCollapseClick = () => {
    if (lib.scrDevices()) {
      setCollapseOpen(!collapseOpen)
    }
  }

  useEffect(() => {
    setSelectedMiles(maxSliderValue)
    setValue('milesNCashInputMiles', maxSliderValue)
    const handleResize = () => {
      if (!lib.scrDevices()) {
        setCollapseOpen(true)
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    mbLoggedIn = !!milesNBonusAccount?.fFN

    if (!mbLoggedIn) {
      return
    }

    setTimeout(() => {
      setSelectedMiles(maxSliderValue)
      setValue('milesNCashInputMiles', maxSliderValue)
    }, 0)
  }, [milesNBonusAccount])

  const calculateHasRemainingCost = () => {
    if (totalPriceToMiles() > selectedMiles) {
      return true
    }
    return false
  }

  useEffect(() => {
    if (!mbData?.redeemEnabled) {
      setTimeout(() => {
        setSelectedMiles(totalPriceToMiles())
        setValue('milesNCashInputMiles', totalPriceToMiles())
      })
    }
  }, [totalPrice])

  return (
    <>
      <div className="b-miles-n-cash">
        <div className="b-miles-n-cash__title" onClick={handleCollapseClick}>
          {dictionary.milesNCashLabel}
          <div className="b-miles-n-cash__title__tag">
            {dictionary.milesNCashTagLabel}
          </div>
          <div
            className={`b-miles-n-cash__title__arrow ${
              collapseOpen ? 'open' : ''
            }`}>
            <i className="icon-arrow-down arrow"></i>
          </div>
        </div>

        <Collapse in={collapseOpen}>
          <div className="b-miles-n-cash__subtitle">
            {dictionary.milesNCashSubtitle}
          </div>

          {hasOffer && (
            <div className="b-miles-n-cash__offer">
              <div className="b-miles-n-cash__offer__icon">
                <i className="icon icon-speaker"></i>
              </div>
              <div className="b-miles-n-cash__offer__content">
                <div className="b-miles-n-cash__offer__title">
                  {dictionary.milesNCashOfferTitle}
                </div>
                <div className="b-miles-n-cash__offer__text">
                  {dictionary.milesNCashOfferSubtitle}
                </div>
              </div>
            </div>
          )}

          {!mbLoggedIn && (
            <div className="b-miles-n-cash__wrapper b-miles-n-cash__wrapper--logged-out">
              <div className="b-miles-n-cash__left">
                <span className="b-miles-n-cash__text">
                  {dictionary.milesNCashLoggedOutText}
                </span>
              </div>
              <div className="b-miles-n-cash__right">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleOpenLoginModal}
                  title={dictionary.milesNCashLoginButton}>
                  {dictionary.milesNCashLoginButton}
                </button>
              </div>
            </div>
          )}

          {mbLoggedIn && (
            <div className="b-miles-n-cash__wrapper">
              {isLoading && (
                <div className="b-miles-n-cash__loading">
                  <CircularProgress />
                </div>
              )}
              <div className="b-miles-n-cash__left cola-lg-2 ">
                <img
                  className="b-miles-n-cash__tier-image"
                  src={milesNBonusAccount.tierCardLogo?.url}
                  alt={milesNBonusAccount.tierCardLogo?.alt}
                  height={milesNBonusAccount.tierCardLogo?.height}
                  width={milesNBonusAccount.tierCardLogo?.width}
                />
                <div className="b-miles-n-cash__account">
                  <span className="b-miles-n-cash__account__miles">
                    {maxSliderValue.toLocaleString(lang)}{' '}
                    {dictionary.milesNCashSliderMilesLabel}
                  </span>
                  <span className="b-miles-n-cash__account__tier">
                    ({milesNBonusAccount.tierName})
                  </span>
                </div>
              </div>
              <div className="b-miles-n-cash__right cola-lg-10">
                {!mbData.redeemEnabled && (
                  <>
                    <div className="b-miles-n-cash__slider-label">
                      {dictionary.milesNCashSliderTitle}
                    </div>
                    <div className="b-miles-n-cash__slider-wrapper">
                      <div className="b-miles-n-cash__slider">
                        <button
                          type="button"
                          onClick={(e) => handleButtonClick(e, 'decrement')}>
                          <div>-</div>
                        </button>
                        <Slider
                          className="b-miles-n-cash__slider__input"
                          value={
                            typeof selectedMiles === 'number'
                              ? selectedMiles
                              : 0
                          }
                          onChange={handleSliderChange}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) =>
                            value.toLocaleString(lang)
                          }
                          aria-labelledby="slider"
                          min={minSliderValue}
                          max={maxSliderValue}
                          sx={{
                            height: 8,
                          }}
                        />
                        <button
                          type="button"
                          onClick={(e) => handleButtonClick(e, 'increment')}>
                          <div>+</div>
                        </button>
                      </div>

                      <div className="b-miles-n-cash__slider__labels">
                        <div className="b-miles-n-cash__slider__labels__min">
                          {minSliderValue.toLocaleString(lang)}{' '}
                          {dictionary.milesNCashSliderMilesLabel}
                        </div>
                        <div className="b-miles-n-cash__slider__labels__max">
                          {maxSliderValue.toLocaleString(lang)}{' '}
                          {dictionary.milesNCashSliderMilesLabel}
                        </div>
                      </div>
                    </div>
                    <div className="b-miles-n-cash__input-group">
                      <span className="b-miles-n-cash__input-group__label">
                        {dictionary.milesNCashMilesInputTitle}
                      </span>
                      <TextField
                        name="milesNCashInputMiles"
                        label={dictionary.milesNCashMilesInputLabel}
                        control={control}
                        type="text"
                        onChange={handleTextFieldChange}
                        variant="outlined"
                        cleaveOptions={{
                          numeral: true,
                          numeralDecimalMark: ',',
                          delimiter: '.',
                          onValueChanged: (e) => {
                            const formattedValue =
                              e.target.rawValue.toLocaleString(lang)
                            e.target.value = formattedValue
                          },
                        }}
                        error={inputError ? { message: inputError } : null}
                      />
                    </div>
                  </>
                )}
                {mbData.redeemEnabled && (
                  <div className="b-miles-n-cash__redeemed">
                    <span className="b-miles-n-cash__redeemed__title">
                      {dictionary.milesNCashThankYouTitle}
                    </span>
                    <span className="b-miles-n-cash__redeemed__text">
                      {dictionary.milesNCashThankYouSubtitle}
                    </span>
                  </div>
                )}
                <div className="b-miles-n-cash__separator"></div>
                <div className="row">
                  <div className="col-lg-8">
                    <div className="b-miles-n-cash__total">
                      <div className="b-miles-n-cash__total__value">
                        <span className="b-miles-n-cash__total__value--currency">
                          -€
                        </span>
                        {!!selectedMiles && (
                          <span className="b-miles-n-cash__total__value--amount">
                            {priceToLocale(selectedMiles / conversionRate)}
                          </span>
                        )}
                      </div>
                      {!!selectedMiles && (
                        <div className="b-miles-n-cash__total__miles">
                          {dictionary.milesNCashReedemLabel}{' '}
                          {selectedMiles.toLocaleString(lang)}{' '}
                          {dictionary.milesNCashMilesLabel}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="b-miles-n-cash__button">
                      <button
                        type="button"
                        className={
                          'btn ' +
                          (mbData.redeemEnabled
                            ? 'btn-outline-primary'
                            : 'btn-primary')
                        }
                        onClick={(e) => handleMilesNCashButton(e)}>
                        {mbData.redeemEnabled
                          ? dictionary.milesNCashEditButtonText
                          : dictionary.milesNCashRedeemButtonText}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Collapse>
      </div>
    </>
  )
}

export default MilesNCash
